import { usePlayer } from "../app/services";
import { useStore } from "../lib/store";
import { formatClock } from "../lib/time";
import type { Block } from "../playback/blocks";

/** What is on air now — the host's script while a voice plays, the track otherwise —
 * and the queue, every host script readable, as the Mac inspector shows it. */
export function NowPlaying() {
  const { engine } = usePlayer();
  const state = useStore(engine);
  const current = state.blocks[state.index] ?? null;

  if (!current) {
    return (
      <div className="empty">
        <p>Nothing is playing.</p>
        <p className="muted">Start a station or play a playlist from the library.</p>
      </div>
    );
  }

  return (
    <div className="now-playing">
      <p className="eyebrow">{state.title}</p>
      {current.kind === "track" ? (
        <div className="now-track">
          {current.artworkURL ? (
            <img src={current.artworkURL} alt="" className="artwork" />
          ) : (
            <div className="artwork placeholder" />
          )}
          <h3>{current.title}</h3>
          <p className="muted">{current.artist}</p>
        </div>
      ) : (
        <div className="now-voice">
          <p className="eyebrow">Host</p>
          <p className="script">{current.script || current.title}</p>
        </div>
      )}
      {state.status === "waiting" && (
        <p className="notice">Waiting for the next batch from the producer…</p>
      )}
      {state.notice && (
        <button type="button" className="notice" onClick={() => engine.dismissNotice()}>
          {state.notice}
        </button>
      )}
      <h4>Up next</h4>
      <ol className="queue">
        {state.blocks.map((block, index) =>
          index < state.index ? null : (
            <QueueRow
              key={block.id}
              block={block}
              isCurrent={index === state.index}
              onPlay={() => void engine.jump(index)}
            />
          ),
        )}
      </ol>
    </div>
  );
}

function QueueRow({
  block,
  isCurrent,
  onPlay,
}: {
  block: Block;
  isCurrent: boolean;
  onPlay: () => void;
}) {
  return (
    <li className={isCurrent ? "current" : ""}>
      <button type="button" onClick={onPlay} className="queue-row">
        {block.kind === "track" ? (
          <>
            <span className="kind">♪</span>
            <span className="grow">
              <strong>{block.title}</strong>
              <span className="muted"> · {block.artist}</span>
            </span>
            <span className="muted">{formatClock(block.duration)}</span>
          </>
        ) : (
          <>
            <span className="kind">◉</span>
            <span className="grow script-preview">{block.script || block.title}</span>
          </>
        )}
      </button>
    </li>
  );
}
